import { ReactNode, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  ISetAccessCardForUserData,
  ISetAccessCardForUserVars,
  SET_ACCESS_CARD_FOR_USER,
} from '../../graphql/users/setAccessCardForUser'
import AccessCardInput from './AccessCardInput'
import { useForm, useWatch } from 'react-hook-form'
import { useMutation } from '@apollo/client'
import { IAccessCardFormat } from '../../graphql/users/queryAccessCardFormat'
import { toast } from 'react-toastify'
import Error from '../Shared/Error'
import { CircularProgress } from '@mui/material'
import CheckIcon from '@mui/icons-material/Check'
import UnassignUserAccessCard from '../UserDetails/UnassignUserAccessCard'
import { IAccessCard } from '../../graphql/users/queryUser'
import { SectionActions, SectionButton } from '../Shared/SectionButton'

type FormData = {
  cardNumber: string
}

interface IAccessCardNumberFormProps {
  login: string
  cardFormat?: IAccessCardFormat
  formatLabel?: ReactNode
  refetch: () => void
  accessCard?: IAccessCard
  allowRemove?: boolean
  callback?: () => void
  extraActions?: ReactNode
}

const AccessCardNumberForm = (props: IAccessCardNumberFormProps) => {
  const {
    login,
    refetch,
    cardFormat,
    formatLabel,
    accessCard,
    callback,
    allowRemove,
    extraActions,
  } = props
  const name = cardFormat?.name
  const type = cardFormat?.type ?? accessCard?.type
  const { t } = useTranslation(['access_card'])
  const [errorMessage, setErrorMessage] = useState('')
  const successNotify = (message: string) => toast.dark(message)
  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<FormData>()

  const onSetAccessCardCompleted = (data: ISetAccessCardForUserData) => {
    const { status, code, error } = data.setAccessCardForUser
    if (status) {
      successNotify(`${t('user_details:access_card_change_success')}`)
      refetch()
      callback && callback()
    } else {
      error &&
        setErrorMessage(
          t(`code:${code}`) === `${code}` ? `${error}` : t(`code:${code}`),
        )
    }
  }

  const [setAccessCardForUser, { loading }] = useMutation<
    ISetAccessCardForUserData,
    ISetAccessCardForUserVars
  >(SET_ACCESS_CARD_FOR_USER, {
    onCompleted: onSetAccessCardCompleted,
    onError: error => console.log(error),
  })

  const handleSaveCard = async (data: FormData) => {
    const { cardNumber } = data
    if (!type) return
    await setAccessCardForUser({
      variables: {
        login,
        cardNumber: cardNumber?.replace('.', ','),
        cardType: type,
        cardFormat: name,
      },
    })
  }

  useEffect(() => {
    accessCard?.number &&
      setValue('cardNumber', String(accessCard?.number).replace(',', '.'))
  }, [accessCard?.number])

  const savedNumber = accessCard?.number
    ? String(accessCard.number).replace(',', '.')
    : ''
  const currentNumber = String(useWatch({ control, name: 'cardNumber' }) ?? '')
  const formatChanged = accessCard?.format?.name !== name
  const saveDisabled =
    !currentNumber.trim() ||
    (currentNumber.trim() === savedNumber && !formatChanged) ||
    loading

  return (
    <>
      <AccessCardInput
        cardFormat={cardFormat}
        formatLabel={formatLabel}
        control={control}
        errors={errors}
      />
      <SectionActions>
        {!!accessCard?.number && allowRemove && (
          <UnassignUserAccessCard login={login} refetch={refetch} />
        )}
        {extraActions}
        <SectionButton
          label={t('btn:save')}
          icon={
            loading ? (
              <CircularProgress size={16} sx={{ color: '#fff' }} />
            ) : (
              <CheckIcon sx={{ fontSize: 17 }} />
            )
          }
          disabled={saveDisabled}
          e2e="save-access-card"
          onClick={() => handleSubmit(handleSaveCard)()}
        />
      </SectionActions>
      {errorMessage && <Error errorMessage={errorMessage} />}
    </>
  )
}

export default AccessCardNumberForm
